import { Vector2, Vector3 } from 'three';

import { clamp, degreesToRadians } from '../../../../utilities/math-helpers';

/**
 * Circular arcs in the horizontal plane, described by a bulge the same way as polylines in DXF:
 * bulge = tan(sweep / 4). They are used to create curves (see conic.ts), e.g. an arc through a point.
 *
 * - 0 is a straight line.
 * - A positive bulge curves to the right of the direction from start to end (counterclockwise),
 *   a negative bulge curves to the left (clockwise).
 * - 1 is a half circle, larger values are more than half a circle.
 *
 * The height changes linearly along the arc.
 */

/** Bulges closer to 0 than this are straight lines. */
const minBulge = 1e-9;

/** Chords shorter than this are straight lines, e.g. a vertical line segment. */
const minChordLength = 1e-9;

/**
 * Limits the sweep to 350 degrees, the arc gets unstable when start and end are close to each other.
 */
export const maxBulge = Math.tan(degreesToRadians(350) / 4);

const clampBulge = (bulge: number) => clamp(bulge, -maxBulge, maxBulge);

/**
 * The angle (radians) the arc sweeps over, positive is counterclockwise.
 */
export const getSweep = (bulge: number) => 4 * Math.atan(bulge);

const isArc = (start: Vector3, end: Vector3, bulge: number) =>
  Math.abs(bulge) > minBulge && Math.hypot(end.x - start.x, end.y - start.y) > minChordLength;

/**
 * Gets the point at t (0 to 1) along the arc, t is proportional to the length along the arc.
 */
export function getArcPoint(
  start: Vector3,
  end: Vector3,
  bulge: number,
  t: number,
  target = new Vector3(),
): Vector3 {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const z = start.z + (end.z - start.z) * t;

  if (!isArc(start, end, bulge)) {
    return target.set(start.x + dx * t, start.y + dy * t, z);
  }

  const chordLength = Math.hypot(dx, dy);
  const halfSweep = 2 * Math.atan(bulge);
  const sinHalfSweep = Math.sin(halfSweep);
  const angle = 2 * halfSweep * (t - 0.5);

  // Offset from the middle of the chord, along the chord and to the right of it.
  // The offset to the right is written as a product of sines, since the difference of the cosines
  // loses precision for flat arcs.
  const along = (0.5 * chordLength * Math.sin(angle)) / sinHalfSweep;
  const right =
    (chordLength * Math.sin(0.5 * (halfSweep + angle)) * Math.sin(0.5 * (halfSweep - angle))) /
    sinHalfSweep;

  const ux = dx / chordLength;
  const uy = dy / chordLength;

  return target.set(
    (start.x + end.x) / 2 + ux * along + uy * right,
    (start.y + end.y) / 2 + uy * along - ux * right,
    z,
  );
}

/**
 * Gets the bulges of the two arcs when splitting an arc at t (0 to 1).
 * Both arcs keep the direction of the original arc.
 */
export function splitBulge(bulge: number, t: number): [number, number] {
  const sweep = getSweep(bulge);
  return [Math.tan((sweep * t) / 4), Math.tan((sweep * (1 - t)) / 4)];
}

/**
 * Gets the bulge of the arc from start to end that passes through the point, in the horizontal plane.
 * Returns 0 if the point is on the line through start and end.
 */
export function getBulgeFromPoints(start: Vector3, through: Vector3, end: Vector3): number {
  const toStart = new Vector2(start.x - through.x, start.y - through.y);
  const toEnd = new Vector2(end.x - through.x, end.y - through.y);
  const chord = new Vector2(end.x - start.x, end.y - start.y);
  const side = chord.cross(new Vector2(through.x - start.x, through.y - start.y));

  if (Math.abs(side) <= minChordLength * chord.length() || toStart.length() === 0) {
    return 0;
  }

  // The angle at the point between start and end is half of the angle outside of the arc
  // (inscribed angle theorem), so the sweep is 2 * (PI - angle).
  const angle = Math.atan2(Math.abs(toStart.cross(toEnd)), toStart.dot(toEnd));
  const bulge = Math.tan((Math.PI - angle) / 2);

  // A positive bulge curves to the right, where the cross product is negative.
  return clampBulge(side < 0 ? bulge : -bulge);
}
